import * as Sentry from "@sentry/react";
import axios from "axios";
import Cookies from "js-cookie";
import posthog from "posthog-js";
import { LC_KEYS } from "../utils/constants";
import { clearImpersonationTab, getStoredToken, isGodMode } from "./impersonation.utils";

// 401s from the auth endpoints themselves are expected (bad credentials on
// login/registration, or logging out) and must never trigger a session wipe.
const AUTH_ENDPOINTS = ["/rest-auth/login/", "/rest-auth/registration/", "/rest-auth/logout/"];

// /users/userinfo/ is the canonical "is my token still valid?" endpoint, so a
// 401 from it is definitive and needs no re-check.
const SESSION_PROBE_PATH = "/users/userinfo/";

let isRedirecting = false;

/** True when the current route is meant to work with no Nudge session at all. */
const isOnLoginFreeRoute = (): boolean => window.location.pathname.startsWith("/setup/");

/**
 * Clears the core auth session data and redirects to login.
 * Used once a 401 has been confirmed to mean the session is actually gone.
 */
export const clearSessionAndRedirect = (): void => {
  // A burst of concurrent requests can 401 together; only redirect once.
  if (isRedirecting) return;

  const godMode = isGodMode();
  // Still clear the stale session below — only the redirect is wrong here.
  const skipRedirect = isOnLoginFreeRoute();
  // Latch only when a redirect is in flight; the clearing below is idempotent.
  if (!skipRedirect) isRedirecting = true;

  // Always drop this tab's impersonation token, even in a normal session — an
  // expired/revoked imp_ token must never linger in sessionStorage (it would
  // keep authenticating and 401-loop through /login → /home).
  clearImpersonationTab();

  // GOD mode is tab-scoped: stop here so the admin's own login (localStorage,
  // shared with other tabs) is left intact.
  if (godMode) {
    if (!skipRedirect) window.location.href = "/login";
    return;
  }

  // Clear core auth keys only
  localStorage.removeItem("token");
  localStorage.removeItem("userInfo");
  Object.values(LC_KEYS).forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear only the Django session cookie. This used to wipe *every* cookie,
  // which also dropped analytics/Intercom cookies and the CSRF token. The login
  // page re-fetches CSRF on mount, so the session cookie is all we need to drop.
  //
  // Not on a login-free route: there, `sessionid` carries the visitor's *portal*
  // grant rather than a stale Nudge login, and dropping it would log them straight
  // out of the portal they just opened.
  if (!skipRedirect) {
    Cookies.remove("sessionid");
  }

  // Reset PostHog tracking
  posthog.reset();

  // Redirect to login page
  if (!skipRedirect) window.location.href = "/login";
};

// A burst of concurrent requests commonly 401s together (e.g. the bootstrap
// calls fired on app load). Share a single re-validation probe across them so we
// hit /users/userinfo/ at most once per burst.
let sessionProbe: Promise<boolean> | null = null;

/**
 * Returns true if the current token still authenticates, false if the session
 * is genuinely gone. Uses a bare axios call (not the shared axiosInstance) so
 * the probe's own response can't recurse back through the 401 interceptor.
 * Inconclusive failures (network error, 5xx, timeout) are treated as "still
 * valid" so a blip never logs the user out.
 */
const isSessionStillValid = (): Promise<boolean> => {
  if (!sessionProbe) {
    const token = getStoredToken();
    if (!token) {
      sessionProbe = Promise.resolve(false);
    } else {
      sessionProbe = axios
        .get(`${process.env.REACT_APP_SERVER_URL}${SESSION_PROBE_PATH}`, {
          headers: { Authorization: `Token ${token}` },
          withCredentials: true,
        })
        .then(() => true)
        .catch((error) => !(axios.isAxiosError(error) && error.response?.status === 401));
      // Allow the probe to run again on the next 401 once this one settles.
      sessionProbe.finally(() => {
        sessionProbe = null;
      });
    }
  }
  return sessionProbe;
};

type UnauthorizedContext = { method?: string; source?: string };

// Drop query + ids/uuids so events group per route in Sentry.
const normalizeEndpoint = (url: string): string =>
  (url || "/")
    .split("?")[0]
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "/:uuid")
    .replace(/\/\d+(?=\/|$)/g, "/:id");

// 401 while userinfo is 200 = token valid but the endpoint rejected it. Log to triage which/when.
const reportFalseNegative401 = (failingUrl: string, { method, source }: UnauthorizedContext): void => {
  const endpoint = normalizeEndpoint(failingUrl);
  const httpMethod = (method || "unknown").toUpperCase();
  console.warn(`[auth] false-negative 401: ${httpMethod} ${endpoint} (userinfo still 200)`);
  Sentry.captureMessage("False-negative 401: endpoint 401 but session still valid", {
    level: "warning",
    tags: { kind: "false_negative_401", endpoint, http_method: httpMethod, source: source || "unknown" },
    extra: { failingUrl, page: window.location.pathname, userAgent: navigator.userAgent },
    fingerprint: ["false-negative-401", endpoint],
  });
};

/**
 * Decide what to do when a request returns 401. Instead of logging out on the
 * first 401 from any endpoint, re-validate the session once against
 * /users/userinfo/ and only clear the session if that also fails. This stops a
 * single transient or endpoint-specific 401 from logging the user out of the
 * whole app.
 */
export const handleUnauthorized = async (failingUrl = "", context: UnauthorizedContext = {}): Promise<void> => {
  if (AUTH_ENDPOINTS.some((endpoint) => failingUrl.includes(endpoint))) {
    return;
  }
  // A 401 from the probe endpoint itself is already definitive.
  if (failingUrl.includes(SESSION_PROBE_PATH)) {
    clearSessionAndRedirect();
    return;
  }
  if (await isSessionStillValid()) {
    reportFalseNegative401(failingUrl, context);
    return;
  }
  clearSessionAndRedirect();
};
